import type { FC } from 'react'
import { Card } from '@mantine/core'
import { DatasetType, type AudienceConfiguration } from '@/lib/domain'
import type { SegmentStage } from '@/features/segment/components/SegmentDetail'
import { SegmentCartCard } from './SegmentCartCard'

export interface SegmentCartItemProps {
    config: AudienceConfiguration
    threadId: string
    // Dataset id → display name (passed through to the card's dataset chips).
    names: Record<string, string>
    onDelete: () => void | Promise<unknown>
    onOpenDetail: (config: AudienceConfiguration, stage: SegmentStage) => void
}

// --- Type-specific definition body — TEMPORARILY DISABLED -------------------
// The cart cards currently show title + datasets + description + footer only.
// To re-enable the per-type definition body: restore the imports below, pass
// `segmentBody(config)` as SegmentCartCard's children, and uncomment this block.
//
// import type { ReactNode } from 'react'
// import { Stack, Text, Divider } from '@mantine/core'
// import {
//     TransactionDefinitionView,
//     UserTransactionBehaviorsView,
// } from '@/features/segment/components/SegmentDefinition'
//
/*
// A labelled definition section in the card body (e.g. "Behavior", "Attributes").
const DefinitionSection: FC<{ label: string; children: ReactNode }> = ({
    label,
    children,
}) => (
    <Stack gap={4} mt={4}>
        <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
            {label}
        </Text>
        {children}
    </Stack>
)

// Renders the per-type definition body for a cart segment. The chrome (title,
// dataset chips, description, reach, footer) is uniform and lives in
// SegmentCartCard; only this body varies by audience type.
function segmentBody(config: AudienceConfiguration): ReactNode {
    if (config.dataset_type === DatasetType.USER_ATTRIBUTE) {
        return config.users_with_attributes ? (
            <DefinitionSection label='Attributes'>
                <TransactionDefinitionView
                    definition={config.users_with_attributes}
                />
            </DefinitionSection>
        ) : null
    }
    if (config.dataset_type === DatasetType.TRANSACTION) {
        return config.users_with_transactions?.length ? (
            <DefinitionSection label='Behavior'>
                <UserTransactionBehaviorsView
                    behaviors={config.users_with_transactions}
                />
            </DefinitionSection>
        ) : null
    }
    // Crossed: both sides, split by a divider. The two datasets are already shown
    // as chips under the title, so the sections only carry their definitions.
    return (
        <>
            {config.users_with_transactions?.length ? (
                <DefinitionSection label='Transaction'>
                    <UserTransactionBehaviorsView
                        behaviors={config.users_with_transactions}
                    />
                </DefinitionSection>
            ) : null}
            <Divider
                my={2}
                variant='dashed'
                label='crossed with'
                labelPosition='center'
            />
            {config.users_with_attributes ? (
                <DefinitionSection label='User Attribute'>
                    <TransactionDefinitionView
                        definition={config.users_with_attributes}
                    />
                </DefinitionSection>
            ) : null}
        </>
    )
}
*/

// One cart segment, rendered with uniform chrome (type-specific body disabled).
export const SegmentCartItem: FC<SegmentCartItemProps> = ({
    config,
    threadId,
    names,
    onDelete,
    onOpenDetail,
}) => {
    const knownType =
        config.dataset_type === DatasetType.USER_ATTRIBUTE ||
        config.dataset_type === DatasetType.TRANSACTION ||
        config.dataset_type === DatasetType.CROSSED
    if (!knownType) {
        return <Card withBorder>Unknown configuration</Card>
    }
    return (
        <SegmentCartCard
            config={config}
            threadId={threadId}
            names={names}
            onDelete={onDelete}
            onOpenDetail={onOpenDetail}
        />
    )
}
